import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ContactSupport,
  HelpOutline
} from '@mui/icons-material';
import Header from '../components/Header';

const FAQDisplay = () => {
  const [faqData, setFaqData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchFAQData();
  }, []);

  const fetchFAQData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/termsconditions');
      const data = await response.json();
      //console.log('Fetched FAQ data:', data);
      if (data.faq) {
        setFaqData(data.faq);
      }
    } catch (err) {
      console.error('Error fetching FAQ:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'primary',
      account: 'secondary',
      billing: 'success',
      technical: 'warning',
      privacy: 'info',
      terms: 'error'
    };
    return colors[category] || 'default';
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box textAlign="center">
          <ContactSupport sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading FAQs...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (

    <div className="about-ksm-page">
      <Header />
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4, marginTop: 15 }}>

        <Container maxWidth="md">
          {/* Header */}
          <Box textAlign="center" mb={4}>

            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {faqData?.title || 'Frequently Asked Questions'}
            </Typography>

          </Box>

          {/* FAQ Items - All answers visible */}
          <Box sx={{ mb: 6 }}>
            {faqData?.items?.map((item, index) => (
              <Paper
                key={index}
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  }
                }}
              >
                {/* Question and Category */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>

                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        lineHeight: 1.4
                      }}
                    >
                      {item.question}
                    </Typography>
                  </Box>
                </Box>

                {/* Answer - Always visible */}
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    '& p': {
                      mb: 1.5,
                      lineHeight: 1.6,
                      color: 'text.primary'
                    },
                    '& ul, & ol': {
                      pl: 3,
                      mb: 1.5
                    },
                    '& li': {
                      mb: 1,
                      lineHeight: 1.5
                    },
                    '& strong': {
                      fontWeight: 500,
                      color: 'text.primary'
                    },
                    '& a': {
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              </Paper>
            ))}
          </Box>

          {/* Empty State */}
          {!faqData?.items || faqData.items.length === 0 ? (
            <Paper
              sx={{
                textAlign: 'center',
                py: 8,
                px: 3,
                borderRadius: 3,
                bgcolor: 'background.paper'
              }}
            >
              <HelpOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No FAQ items available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later for frequently asked questions
              </Typography>
            </Paper>
          ) : null}

          {/* Contact Section */}

        </Container>
      </Box>
    </div>
  );
};

export default FAQDisplay;